import StudentSubmissionChatPanel from 'components/Chat/StudentSubmissionChatPanel';
import FeedbackRegenerating from 'components/Grading/FeedbackRegenerating';
import FileViewer from 'components/Grading/FileViewer';
import FeedbackRenderer from 'components/StudentViewSubmission/FeedbackRenderer';
import Header from 'components/StudentViewSubmission/Header';
import InquireControls from 'components/StudentViewSubmission/InquireControls';
import SlidingPanels from 'components/StudentViewSubmission/SlidingPanels';
import { useApp } from 'contexts/app';
import { useMemo } from 'react';
import { Box, Container, PDFViewer, SheetViewer, Stack, styled, VideoViewer } from 'shell/components';
import { getS3FileURL } from 'utils/helper';

const Desktop = ({
    hasUnreadSubmissionChat,
    isChatOpen,
    selectedFeedbackId,
    setSelectedFeedbackId,
    selectedInlineCommentId,
    setSelectedInlineCommentId,
    isDocumentFullScreen,
    setIsDocumentFullScreen,
    isGenerating,
    currentSubmission,
    highlights,
    onAssignmentChatChange,
    onCloseChat,
    onOpenChat,
    resolveOpenedAssignmentChat,
}) => {
    const { currentAssignment } = useApp();

    const currentFile = currentSubmission.files[0];
    const url = useMemo(() => getS3FileURL(currentSubmission.files[0]), [currentSubmission]);
    const renderableHighlights = highlights.map(({ id, value }) => {
        if (typeof value === 'string') return { id, value };
        return { id: id, index: value.index, value: value.highlight };
    });

    return (
        <CustomContainer>
            <Header />
            <CustomStack direction={'row'}>
                <LeftStack isDocumentFullScreen={isDocumentFullScreen}>
                    {currentFile && (
                        <Box sx={{ height: '100%' }}>
                            {currentFile.type === 'application/pdf' ? (
                                <PDFViewer
                                    url={url}
                                    highlights={renderableHighlights}
                                    inlineComments={currentSubmission.ai.inline_comments}
                                    setInlineComments={() => {}}
                                    selectedInlineCommentId={selectedInlineCommentId}
                                    setSelectedInlineCommentId={setSelectedInlineCommentId}
                                    isFullScreen={isDocumentFullScreen}
                                    setIsFullScreen={setIsDocumentFullScreen}
                                    isStudent
                                />
                            ) : currentFile.type === 'text/csv' ? (
                                <SheetViewer highlights={renderableHighlights} url={url} />
                            ) : currentFile.type.startsWith('video/') ||
                              currentFile.type.startsWith('application/mp4') ? (
                                <VideoViewer
                                    highlights={renderableHighlights}
                                    mimeType={currentFile.type}
                                    transcript={currentFile.transcript}
                                    url={url}
                                />
                            ) : (
                                <FileViewer docUrl={url} fileType={currentFile.type} />
                            )}
                        </Box>
                    )}
                </LeftStack>
                <RightStack isDocumentFullScreen={isDocumentFullScreen}>
                    {isGenerating ? (
                        <FeedbackRegenerating sx={{ top: '40%' }} />
                    ) : (
                        <SlidingPanels
                            leftPanel={
                                <>
                                    <InquireControls hasUnreadChat={hasUnreadSubmissionChat} onOpenChat={onOpenChat} />
                                    <FeedbackRenderer
                                        assignment={currentAssignment}
                                        currentSubmission={currentSubmission}
                                        selectedFeedbackId={selectedFeedbackId}
                                        setSelectedFeedbackId={setSelectedFeedbackId}
                                    />
                                </>
                            }
                            rightPanel={
                                <StudentSubmissionChatPanel
                                    currentSubmission={currentSubmission}
                                    isOpen={isChatOpen}
                                    onBack={onCloseChat}
                                    onChatUpdated={onAssignmentChatChange}
                                    resolveChat={resolveOpenedAssignmentChat}
                                />
                            }
                            showRightPanel={isChatOpen}
                        />
                    )}
                </RightStack>
            </CustomStack>
        </CustomContainer>
    );
};

const CustomContainer = styled(Container)(({ theme }) => {
    return {
        position: 'absolute',
        top: 0,
        zIndex: theme.zIndex.drawer + 1,
        minHeight: '100%',
        padding: 0,
        background: theme.palette.ensightful.neutral[100],
    };
});

const CustomStack = styled(Stack)(() => {
    return {
        position: 'absolute',
        top: '49px',
        width: '100%',
        height: 'calc(100vh - 49px)',
    };
});

const LeftStack = styled(Stack)(({ theme, isDocumentFullScreen }) => {
    return {
        position: 'relative',
        width: isDocumentFullScreen ? '100%' : '60%',
        background: theme.palette.ensightful.shades[0],
        transition: 'width 150ms',
        ...(!isDocumentFullScreen && {
            borderRight: `1px solid ${theme.palette.ensightful.neutral[700]}`,
        }),
    };
});

const RightStack = styled(Stack)(({ theme, isDocumentFullScreen }) => {
    return {
        width: isDocumentFullScreen ? 0 : '40%',
        background: theme.palette.ensightful.shades[0],
        overflow: 'auto',
        transition: 'width 150ms',
    };
});

export default Desktop;
